/**
 * E.164 for the two countries P1 serves (spec §5.4: India and US only). Hand-written rather than
 * libphonenumber-js: the repo has no phone library, two countries' rules fit in a few lines, and this
 * module rides the shared barrel into the web bundle. P4 (world) adds the library with its countries.
 *
 * Review Focus 2: one number arrives as +14155550100, 14155550100 or 4155550100 (and +919…, 919…,
 * 09…), and every form must land on the one E.164 the owner verified.
 */
import { PHONE_DIAL_CODES, type PhoneCountry } from './constants.js';

/** NANP: the area code and the exchange never start with 0 or 1. */
const US_NATIONAL = /^[2-9]\d{2}[2-9]\d{6}$/;
/** India: ten digits with no leading 0 (the 0 is the trunk prefix, never part of the number). */
const IN_NATIONAL = /^[1-9]\d{9}$/;

const national = (country: PhoneCountry, digits: string): string | null =>
  (country === 'US' ? US_NATIONAL : IN_NATIONAL).test(digits) ? `+${PHONE_DIAL_CODES[country]}${digits}` : null;

/** Digits that carry their country code: 11 starting 1, or 12 starting 91. */
function withCountryCode(digits: string): string | null {
  if (digits.length === 11 && digits.startsWith('1')) return national('US', digits.slice(1));
  if (digits.length === 12 && digits.startsWith('91')) return national('IN', digits.slice(2));
  return null;
}

/**
 * The E.164 of `raw`, or null. Spaces, dashes, dots and brackets are ignored; `+` and `00` mean the
 * country code follows. A bare national number needs `defaultCountry` (the dialled Bash number's
 * country, for a caller ID): ten digits are national there, and in India an 11-digit number with the
 * trunk 0 is too.
 */
export function normalizeE164(raw: string | null | undefined, defaultCountry?: PhoneCountry): string | null {
  if (typeof raw !== 'string') return null;
  const s = raw.trim().replace(/[\s\-.()]/g, '');
  if (!s) return null;
  if (s.startsWith('+')) return /^\+\d+$/.test(s) ? withCountryCode(s.slice(1)) : null;
  if (!/^\d+$/.test(s)) return null;
  if (s.startsWith('00')) return withCountryCode(s.slice(2));
  const coded = withCountryCode(s);
  if (coded) return coded;
  if (defaultCountry === 'US' && s.length === 10) return national('US', s);
  if (defaultCountry === 'IN' && s.length === 10) return national('IN', s);
  if (defaultCountry === 'IN' && s.length === 11 && s.startsWith('0')) return national('IN', s.slice(1));
  return null;
}

export function countryOfE164(e164: string): PhoneCountry | null {
  if (/^\+1\d{10}$/.test(e164)) return 'US';
  if (/^\+91\d{10}$/.test(e164)) return 'IN';
  return null;
}

/** For logs (the frame's rule): the country code and the last four digits, never the rest. */
export function maskE164(e164: string): string {
  if (!/^\+\d{8,15}$/.test(e164)) return '+••••';
  const cc = e164.startsWith('+91') ? '+91' : e164.startsWith('+1') ? '+1' : e164.slice(0, 2);
  return `${cc}••••••${e164.slice(-4)}`;
}

/** A +1 number (the US and Canada) rings at once; an international leg spends 10 to 20 s reaching the handset first. */
export const PHONE_DIAL_TIMEOUT_SECS = { nanp: 30, international: 55 } as const;

/**
 * How long a dial to `e164` rings before the carrier gives up, for every call Bash places (the verification call and
 * a ring). The founder's +91 verification call (2026-10-15) was cancelled at 30 s, before his phone had rung long
 * enough to pick up: SIP 487 at +30 s, never answered.
 */
export function dialTimeoutSecs(e164: string): number {
  return e164.startsWith('+1') ? PHONE_DIAL_TIMEOUT_SECS.nanp : PHONE_DIAL_TIMEOUT_SECS.international;
}
